import type {
  InjectionProtocol,
  ProtocolBrand,
  ProtocolWarning,
} from "../types";

/**
 * Ovidrel (choriogonadotropin alfa), the recombinant hCG trigger shot.
 *
 * The first protocol because it is the sharpest case #2267 has: retrieval is
 * scheduled 34-36 hours after the injection and half an hour of drift affects egg
 * maturity, so the moment carries real consequence and real anxiety. It is also a
 * single one-time dose, which keeps the first pilot's surface small — there is no
 * schedule to reason about, no missed-dose recovery, no titration.
 *
 * Restated from `content/data/medications/trigger-shots.json` (the `Ovidrel`
 * entry). Wording is the content's, verbatim in both locales; what is added here
 * is the structure the prose cannot carry — step kind, confirmation, escalation.
 *
 * Authored rather than parsed. The two locales do not share markers: English
 * carries `Step 1:`…`Step 6:`, the French `frBody` drops them and opens straight
 * into `Timing essentiel -`. A parser would have to guess at step boundaries in
 * French, and guessing is not acceptable on a procedure a patient follows with a
 * needle in her hand.
 */

/**
 * The names this trigger reaches patients under, and where.
 *
 * Same molecule, two trade names, split by market and not by language. Kept
 * beside the protocols rather than inlined so the drift test can hold both
 * against the content entry's `availability` in one place.
 */
const OVIDREL_BRANDS = [
  {
    name: "Ovidrel",
    markets: [
      "US",
      "CA",
      "AU",
      "NZ",
      "BR",
      "MX",
      "AR",
      "MY",
      "SG",
      "PH",
      "TH",
      "ID",
      "HK",
      "KR",
    ],
  },
  { name: "Ovitrelle", markets: ["EU", "GB", "CH", "VN", "IN"] },
] as const satisfies readonly ProtocolBrand[];

/**
 * Guidance carried by the injection step, not a symptom she reports.
 *
 * `caution` because it is an instruction: it is said alongside the step, and
 * nothing about it should halt a procedure that is going correctly. It used to
 * be `stop` with `escalation: "none"`, which meant raising it paused the whole
 * session and then offered nowhere to go — the one shape a stop warning must
 * never have. If she has actually given a partial dose, that is
 * `ovidrel.spill-or-incomplete-dose`, which routes to the clinic.
 *
 * It sits on the injection step rather than on aftercare because that is where
 * it can still change what she does.
 */
const SINGLE_DOSE_CAUTION: ProtocolWarning = {
  id: "ovidrel.do-not-split-dose",
  severity: "caution",
  escalation: "none",
  copy: {
    en: "Do not split the dose - {brand} is a single-dose injection; the entire contents must be administered at once.",
    fr: "Ne pas fractionner la dose - {brand} est une injection en dose unique ; toute la dose doit être administrée en une seule fois.",
  },
  sourceSegment:
    "⚠ Do not split the dose - Ovidrel is a single-dose injection; the entire contents must be administered at once.",
};

/** Symptoms she may report, true whichever device is dispensed. */
const MEDICATION_WARNINGS: ProtocolWarning[] = [
  {
    id: "ovidrel.spill-or-incomplete-dose",
    // A `stop`, because a dose that may not have gone in is not something to
    // walk past — and `clinic-soon`, because it is a call to make rather than a
    // race to run. The content said "IMMEDIATELY" in capitals, which reads as
    // an emergency to someone already frightened (Anaïs, 2026-09-22).
    severity: "stop",
    escalation: "clinic-soon",
    copy: {
      en: "Spill or incomplete dose - If you spill any medication or suspect you did not receive the full dose, call your clinic as soon as you can. Do not attempt a second injection without guidance.",
      fr: "Renversement ou dose incomplète - Si vous renversez du produit ou suspectez une dose incomplète, appelez votre centre dès que possible. Ne tentez pas une seconde injection sans avis médical.",
    },
    sourceSegment:
      "⚠ Spill or incomplete dose - If you spill any medication or suspect you did not receive the full dose, call your clinic as soon as you can. Do not attempt a second injection without guidance.",
  },
  {
    id: "ovidrel.breathing-or-allergic-reaction",
    // Emergency services, and the sentence says so. It used to read "Contact
    // your clinic for severe bloating, difficulty breathing, or signs of
    // allergic reaction" — one sentence, three symptoms, one destination, and
    // the destination was wrong for the one that kills quickly. Anaphylaxis is
    // not a phone call to a fertility clinic (Anaïs, 2026-09-22), and the
    // content was changed to say so before this restated it.
    severity: "stop",
    escalation: "emergency",
    copy: {
      en: "Seek urgent help - Difficulty breathing or signs of an allergic reaction need emergency services straight away.",
      fr: "Aide urgente - Des difficultés respiratoires ou des signes de réaction allergique nécessitent d'appeler les secours tout de suite.",
    },
    sourceSegment:
      "⚠ Seek urgent help - Difficulty breathing or signs of an allergic reaction need emergency services straight away.",
  },
  {
    id: "ovidrel.severe-bloating",
    // The other half of that sentence, and it keeps the clinic: bloating after
    // a trigger is the beginning of OHSS, which her own clinic manages and
    // wants to hear about the same day.
    severity: "stop",
    escalation: "clinic-now",
    copy: {
      en: "Severe bloating - Call your clinic the same day for severe bloating.",
      fr: "Ballonnements sévères - Appelez votre centre le jour même en cas de ballonnements sévères.",
    },
    sourceSegment:
      "⚠ Severe bloating - Call your clinic the same day for severe bloating.",
  },
];

/**
 * From the content's own alias list, minus the trade names it repeats.
 *
 * "Ovitrelle" is in the content's aliases and is a brand here, with markets; a
 * name that belongs on a box belongs in `brands`, where it can be checked
 * against where it is actually sold.
 */
const OVIDREL_ALIASES = [
  "Choriogonadotropin alfa",
  "Choriogonadotropine alfa",
  "hCG recombinant",
  "Recombinant hCG",
] as const;

/**
 * One strength, both devices: 250 micrograms in 0.5 mL, fixed and
 * non-adjustable. The content also quotes the IU equivalent (6,500 IU); a
 * record in IU is still asked about, because the coach never converts.
 */
const OVIDREL_DOSE = { unit: "mcg", strengths: [250] } as const;

export const OVIDREL_PEN_PROTOCOL: InjectionProtocol = {
  medicationSlug: "ovidrel",
  /**
   * Restated from the `availability` block of the same content entry. Both
   * devices carry the full list: the trade name is decided by market and the
   * device by prescription, and they are independent axes — Ovitrelle ships as
   * a pen and as a pre-filled syringe, and so does Ovidrel.
   */
  brands: OVIDREL_BRANDS,
  aliases: OVIDREL_ALIASES,
  indication: "trigger",
  dose: OVIDREL_DOSE,
  form: "pen",
  prescriptionForms: ["pre_filled_pen"],
  route: "subcutaneous",
  version: "2026-09-22.1",
  derivedFrom: "content/data/medications/trigger-shots.json#ovidrel",
  steps: [
    {
      // Timing is a step, not a preamble: it is the one instruction that is
      // useless once the procedure is under way, and the content leads with it.
      //
      // Shorter than the sentence it comes from, deliberately. The content adds
      // that retrieval is 34-36 hours away and to set alarms — true, and not
      // what she needs while holding a pen. A protocol step may **omit** from
      // the approved content; it may never **add** to it, which is the property
      // `ovidrel-protocol.test.ts` now holds. Dropping the interval also settles
      // a standing note about it: the number is the clinic's, and a coach that
      // states one is a coach deriving it.
      id: "ovidrel.pen.critical-timing",
      kind: "verify",
      requiresConfirmation: true,
      copy: {
        en: "Critical timing - Inject at the EXACT time your clinic prescribed.",
        fr: "Timing essentiel - Injectez à l'heure EXACTE prescrite par votre centre.",
      },
    },
    {
      id: "ovidrel.pen.prepare",
      kind: "prepare",
      requiresConfirmation: false,
      copy: {
        en: "Prepare - Wash hands and check the expiration date on the pen. {brand} is prefilled - no mixing or reconstitution needed.",
        fr: "Préparer - Lavez-vous les mains et vérifiez la péremption du stylo. {brand} est prérempli - aucune préparation ni reconstitution nécessaire.",
      },
    },
    {
      id: "ovidrel.pen.ready-the-pen",
      kind: "prepare",
      requiresConfirmation: false,
      copy: {
        en: "Ready the pen - Remove the needle cap carefully without touching the needle tip.",
        fr: "Préparer le stylo - Retirez prudemment le capuchon de l'aiguille sans toucher la pointe.",
      },
    },
    {
      id: "ovidrel.pen.clean-site",
      kind: "prepare",
      requiresConfirmation: false,
      copy: {
        en: "Clean the site - Clean a spot on your lower abdomen (2 inches from the navel) with an alcohol swab and let it air-dry completely.",
        fr: "Désinfecter le site - Désinfectez un point sur le bas du ventre (à 5 cm du nombril) à l'alcool et laissez sécher complètement à l'air.",
      },
    },
    {
      id: "ovidrel.pen.inject",
      kind: "irreversible",
      requiresConfirmation: true,
      copy: {
        en: "Inject - Pinch the skin and insert the needle at a 90° angle. Inject the entire contents slowly over 5-10 seconds, then hold for 5 seconds before withdrawing.",
        fr: "Injecter - Pincez la peau et insérez l'aiguille à 90°. Injectez la totalité du contenu lentement sur 5 à 10 secondes, puis maintenez 5 secondes avant de retirer.",
      },
      warnings: [
        {
          id: "ovidrel.pen.malfunction-or-spill",
          severity: "stop",
          escalation: "clinic-soon",
          copy: {
            en: "Pen malfunction or spill - If the pen malfunctions or any medication is spilled, call your clinic as soon as you can rather than attempting a second injection.",
            fr: "Défaut du stylo ou renversement - En cas de dysfonctionnement ou de renversement, appelez votre centre dès que possible plutôt que de tenter une seconde injection.",
          },
          sourceSegment:
            "⚠ Pen malfunction or spill - If the pen malfunctions or any medication is spilled, call your clinic as soon as you can rather than attempting a second injection.",
        },
        SINGLE_DOSE_CAUTION,
      ],
    },
    {
      id: "ovidrel.pen.aftercare",
      kind: "aftercare",
      requiresConfirmation: false,
      copy: {
        en: "After injection - Apply gentle pressure with gauze - do not rub. Dispose of the pen in a sharps container.",
        fr: "Après l'injection - Appliquez une légère pression avec une compresse - sans frotter. Jetez le stylo dans un collecteur.",
      },
      warnings: MEDICATION_WARNINGS,
    },
  ],
};

export const OVIDREL_PRE_FILLED_SYRINGE_PROTOCOL: InjectionProtocol = {
  medicationSlug: "ovidrel",
  /**
   * Restated from the `availability` block of the same content entry. Both
   * devices carry the full list: the trade name is decided by market and the
   * device by prescription, and they are independent axes — Ovitrelle ships as
   * a pen and as a pre-filled syringe, and so does Ovidrel.
   */
  brands: OVIDREL_BRANDS,
  aliases: OVIDREL_ALIASES,
  indication: "trigger",
  dose: OVIDREL_DOSE,
  form: "pre_filled_syringe",
  prescriptionForms: ["pre_filled_syringe"],
  route: "subcutaneous",
  version: "2026-09-22.1",
  derivedFrom: "content/data/medications/trigger-shots.json#ovidrel",
  steps: [
    {
      id: "ovidrel.pre_filled_syringe.critical-timing",
      kind: "verify",
      requiresConfirmation: true,
      copy: {
        en: "Critical timing - Inject at the EXACT time your clinic prescribed.",
        fr: "Timing essentiel - Injectez à l'heure EXACTE prescrite par votre centre.",
      },
    },
    {
      id: "ovidrel.pre_filled_syringe.prepare",
      kind: "prepare",
      requiresConfirmation: false,
      copy: {
        en: "Prepare - Wash hands and check the expiration date. Remove the syringe from refrigeration a few minutes before use to let it warm slightly.",
        fr: "Préparer - Lavez-vous les mains et vérifiez la péremption. Sortez la seringue du réfrigérateur quelques minutes avant l'utilisation pour qu'elle se réchauffe légèrement.",
      },
    },
    {
      // `verify` rather than `prepare`: the patient is being asked to judge the
      // solution and to not adjust the dose. Both are checks she can fail.
      id: "ovidrel.pre_filled_syringe.inspect",
      kind: "verify",
      requiresConfirmation: true,
      copy: {
        en: "Inspect - Remove the needle cap carefully without touching the needle. Confirm the solution is clear and colorless. Do NOT attempt to adjust the dose - the syringe contains the full prescribed amount{dose}.",
        fr: "Inspecter - Retirez prudemment le capuchon de l'aiguille sans la toucher. Vérifiez que la solution est claire et incolore. Ne PAS tenter d'ajuster la dose - la seringue contient la dose complète prescrite{dose}.",
      },
    },
    {
      id: "ovidrel.pre_filled_syringe.clean-site",
      kind: "prepare",
      requiresConfirmation: false,
      copy: {
        en: "Clean the site - Clean a spot on your lower abdomen (2 inches from the navel) with an alcohol swab and let it air-dry completely.",
        fr: "Désinfecter le site - Désinfectez un point sur le bas du ventre (à 5 cm du nombril) à l'alcool et laissez sécher complètement à l'air.",
      },
    },
    {
      id: "ovidrel.pre_filled_syringe.inject",
      kind: "irreversible",
      requiresConfirmation: true,
      copy: {
        en: "Inject - Pinch the skin, insert the needle at a 90° angle, and push the plunger slowly and steadily until the syringe is completely empty. Hold for 5 seconds before withdrawing.",
        fr: "Injecter - Pincez la peau, insérez l'aiguille à 90° et poussez le piston lentement et régulièrement jusqu'à ce que la seringue soit complètement vide. Maintenez 5 secondes avant de retirer.",
      },
      warnings: [
        {
          id: "ovidrel.pre_filled_syringe.malfunction-or-spill",
          severity: "stop",
          escalation: "clinic-soon",
          copy: {
            en: "Syringe malfunction or spill - If you suspect the syringe malfunctioned or any medication was spilled, call your clinic as soon as you can. Do not attempt a second injection without guidance.",
            fr: "Défaut ou renversement - Si vous suspectez un dysfonctionnement de la seringue ou un renversement de produit, appelez votre centre dès que possible. Ne tentez pas une seconde injection sans avis médical.",
          },
          sourceSegment:
            "⚠ Syringe malfunction or spill - If you suspect the syringe malfunctioned or any medication was spilled, call your clinic as soon as you can. Do not attempt a second injection without guidance.",
        },
        SINGLE_DOSE_CAUTION,
      ],
    },
    {
      id: "ovidrel.pre_filled_syringe.aftercare",
      kind: "aftercare",
      requiresConfirmation: false,
      copy: {
        en: "After injection - Apply gentle pressure with gauze - do not rub. Dispose of the syringe in a sharps container.",
        fr: "Après l'injection - Appliquez une légère pression avec une compresse - sans frotter. Jetez la seringue dans un collecteur.",
      },
      warnings: MEDICATION_WARNINGS,
    },
  ],
};

export const OVIDREL_PROTOCOLS = [
  OVIDREL_PEN_PROTOCOL,
  OVIDREL_PRE_FILLED_SYRINGE_PROTOCOL,
] as const;
